import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEditorStore } from '../stores/editorStore';
import { fetchEpisode } from '../api/client';

export const useLoadEpisode = () => {
  const { id, sectionIndex } = useParams<{ id: string; sectionIndex?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const setEpisode = useEditorStore((state) => state.setEpisode);
  const setViewMode = useEditorStore((state) => state.setViewMode);
  const setCurrentSection = useEditorStore((state) => state.setCurrentSection);
  const currentEpisode = useEditorStore((state) => state.currentEpisode);
  const [loading, setLoading] = useState(!currentEpisode || currentEpisode.id !== id);
  const [error, setError] = useState<string | null>(null);
  const hasAppliedUrlState = useRef(false);

  // Apply URL state to store
  const applyUrlState = () => {
    const path = location.pathname;
    if (path.endsWith('/intro')) {
      setViewMode('intro');
    } else if (path.endsWith('/outro')) {
      setViewMode('outro');
    } else if (path.includes('/section/')) {
      setViewMode('section');
      const idx = parseInt(sectionIndex || '0', 10);
      if (!isNaN(idx)) {
        setCurrentSection(idx);
      }
    }
    // Default /episodes/:id goes to intro
    else if (path.match(/\/episodes\/[^/]+$/)) {
      setViewMode('intro');
    }
  };

  // Parse view mode from URL path when URL changes (navigation within app)
  // Note: Only react to pathname and sectionIndex changes, NOT currentEpisode changes
  // Otherwise element updates would trigger this and clear selection
  useEffect(() => {
    if (currentEpisode && currentEpisode.id === id) {
      applyUrlState();
    }
  }, [location.pathname, sectionIndex, id]);

  useEffect(() => {
    // Skip loading if we already have the correct episode in store
    if (currentEpisode && currentEpisode.id === id) {
      setLoading(false);
      // Apply URL state if not already done
      if (!hasAppliedUrlState.current) {
        applyUrlState();
        hasAppliedUrlState.current = true;
      }
      return;
    }

    const loadEpisode = async () => {
      if (!id) {
        navigate('/episodes');
        return;
      }

      setLoading(true);
      setError(null);
      hasAppliedUrlState.current = false;

      try {
        const episode = await fetchEpisode(id);
        
        const sections = (episode.sections || []).map((section: any) => ({
          id: section.id,
          type: section.type || 'content',
          title: section.title,
          order: section.order ?? 0,
          background: section.background || 'default',
          imageUrl: section.imageUrl,
          talkingPoints: section.talkingPoints,
          elements: section.elements || [],
          connections: section.connections || [],
        }));

        setEpisode({
          id: episode.id,
          number: episode.number,
          title: episode.title,
          date: episode.date,
          showTitle: episode.showTitle,
          sections,
        });
        
        // Apply URL state after episode is loaded
        applyUrlState();
        hasAppliedUrlState.current = true;
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load episode');
        setLoading(false);
      }
    };

    loadEpisode();
  }, [id, navigate, setEpisode, currentEpisode]);

  return { loading, error, navigate };
};
